import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';

import Link from '../components/Link';
import Container from '../components/Container';
import ConditionalRendering from '../components/ConditionalRendering';
import Grid from '../components/Grid';
import Form from '../components/Form';
import FormGroup from '../components/FormGroup';
import FormControl from '../components/FormControl';
import FormLabel from '../components/FormLabel';
import PrimaryAction from '../components/PrimaryAction';
import Spacing from '../components/Spacing';
import Text from '../components/Text';

import submitForm from './submitForm';
import schema from './schema';

const defaultProps = {
  disabled: false,
  cookie: '',
  onSubmit: () => {},
  onSuccess: () => {},
};

const propTypes = {
  disabled: PropTypes.bool,
  cookie: PropTypes.string,
  onSubmit: PropTypes.func,
  onSuccess: PropTypes.func,
};

const DownloadProgramForm = ({
  disabled,
  cookie,
  onSubmit,
  onSuccess,
}) => {
  const [remoteErrors, setErrors] = useState([]);
  const [isABoot, setIsBoot] = useState(false);

  const setSuccessCallback = useCallback(() => {
    onSuccess();
  }, [
    onSuccess,
  ]);

  const setErrorCallback = useCallback((e) => {
    setErrors(e);
  }, []);

  const onSubmitCallback = useCallback((value, props) => {
    if (isABoot === false && !disabled) {
      submitForm(value, {
        ...props,
        onSubmit,
        onSuccess: setSuccessCallback,
        setError: setErrorCallback,
        cookie,
      });
    }
  }, [
    isABoot,
    disabled,
  ]);

  return (
    <Form
      validationSchema={schema}
      validateOnChange={false}
      validateOnBlur={false}
      className="flex-1 px-8 py-4 lg:p-0"
      onSubmit={onSubmitCallback}
    >
      {({
        handleChange,
        handleBlur,
        isSubmitting,
        values: {
          firstname,
          lastname,
          email,
          mobilephone,
          city,
          country,
          website, // honeypot field
        },
        errors,
      }) => (
        <Container className="px-1">
          <ConditionalRendering on={disabled}>
            <Spacing bottom={4}>
              <Container className="bg-yellow-200 rounded py-2 px-4 text-yellow-700 text-sm">
                Este formulario está deshabilitado para esta versión.
              </Container>
            </Spacing>
          </ConditionalRendering>
          <ConditionalRendering on={remoteErrors && remoteErrors.length !== 0}>
            <Spacing bottom={6}>
              {remoteErrors.map(({ message }) => (
                <Text
                  key={message}
                  className="text-sm w-full bg-red-600 text-red-100 rounded px-4 py-2 leading-tight"
                >
                  {message}
                </Text>
              ))}
            </Spacing>
          </ConditionalRendering>
          <Grid
            gap={4}
            mdGap={10}
          >
            <Container>
              <FormGroup
                hasError={!!errors.firstname}
              >
                <FormLabel
                  htmlFor="firstname"
                  value="Nombres"
                />
                <FormControl
                  type="text"
                  id="firstname"
                  name="firstname"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={firstname}
                  hasError={!!errors.firstname}
                  disabled={disabled}
                  required
                />
              </FormGroup>
            </Container>
            <Container>
              <FormGroup
                hasError={!!errors.lastname}
              >
                <FormLabel
                  htmlFor="lastname"
                  value="Apellidos"
                />
                <FormControl
                  type="text"
                  id="lastname"
                  name="lastname"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={lastname}
                  hasError={!!errors.lastname}
                  disabled={disabled}
                  required
                />
              </FormGroup>
            </Container>
          </Grid>
          <Grid
            gap={4}
            mdGap={10}
          >
            <Container>
              <FormGroup
                hasError={!!errors.email}
              >
                <FormLabel
                  htmlFor="email"
                  value="Correo electrónico"
                />
                <FormControl
                  type="email"
                  id="email"
                  name="email"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={email}
                  hasError={!!errors.email}
                  disabled={disabled}
                  required
                />
              </FormGroup>
            </Container>
            <Container>
              <FormGroup
                hasError={!!errors.mobilephone}
              >
                <FormLabel
                  htmlFor="mobilephone"
                  value="Número de teléfono móvil"
                />
                <FormControl
                  type="phone"
                  id="mobilephone"
                  name="mobilephone"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={mobilephone}
                  hasError={!!errors.mobilephone}
                  disabled={disabled}
                  required
                />
              </FormGroup>
            </Container>
          </Grid>
          <Grid
            gap={4}
            mdGap={10}
          >
            <Container>
              <FormGroup
                hasError={!!errors.city}
              >
                <FormLabel
                  htmlFor="city"
                  value="Ciudad"
                />
                <FormControl
                  type="text"
                  id="city"
                  name="city"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={city}
                  hasError={!!errors.city}
                  disabled={disabled}
                />
              </FormGroup>
            </Container>
            <Container>
              <FormGroup
                hasError={!!errors.country}
              >
                <FormLabel
                  htmlFor="country"
                  value="País/región"
                />
                <FormControl
                  type="text"
                  id="country"
                  name="country"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={country}
                  hasError={!!errors.country}
                  disabled={disabled}
                />
              </FormGroup>
            </Container>
          </Grid>
          <FormControl
            type="text"
            id="website"
            name="website"
            onChange={(e) => {
              handleChange(e);
              setIsBoot(true);
            }}
            value={website}
            userHide
          />
          <Spacing top={4}>
            <PrimaryAction
              type="submit"
              size="large"
              disabled={isSubmitting || isABoot || disabled}
              loading={isSubmitting}
              block
            >
              Descargar programa
            </PrimaryAction>
            <Spacing top={4}>
              <Text className="text-xs leading-snug text-gray-500">
                Estamos comprometidos con su privacidad. AIBES utiliza la información que
                nos proporciona para contactarlo sobre nuestro contenido, productos y
                servicios relevantes. Puede darse de baja de estas comunicaciones en
                cualquier momento. Para obtener más información, consulte nuestra&nbsp;
                <Link
                  className="underline"
                  href="https://www.aibes.org/nosotros/politica-de-privacidad/?ref=oacct"
                  target="_blank"
                  rel="noopener"
                >
                  Política de privacidad
                </Link>
                .
              </Text>
            </Spacing>
          </Spacing>
        </Container>
      )}
    </Form>
  );
};

DownloadProgramForm.defaultProps = defaultProps;
DownloadProgramForm.propTypes = propTypes;

export default DownloadProgramForm;
